/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { useNavigate } from '@tanstack/react-router';
import React, { forwardRef } from 'react';

import { useAccount } from '~/apis';
import { useTranslation } from '~/app';
import { useIsEmailVerifiedEffective } from '~/app/DebugEmailVerifiedContext';
import { ROUTE_ACCOUNT, ROUTE_ACCOUNT_SUBSCRIPTIONS } from '~/app/routes';
import { DropdownMenuItem, DropdownMenuSeparator } from '~/components/DropdownMenu/DropdownMenu';
import { LanguageSubmenu } from '~/components/LanguageSubmenu';
import PeopleToplistIcon from '~/components/PeopleToplistIcon';
import AccountProfilePicture from '~/components/ProfilePicture/AccountProfilePicture';
import { useSubscriptionLabel } from '~/utils';

import { ReactComponent as EmailIcon } from './email.svg';
import { ReactComponent as LogoutIcon } from './logout.svg';
import { ReactComponent as ProfileIcon } from './profile.svg';

export interface MenuOptionProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isInDropdown?: boolean;
}

export const MenuOption = forwardRef<HTMLButtonElement, MenuOptionProps>(
  ({ icon, label, onClick, isInDropdown = false }, ref) => (
    <button
      ref={ref}
      className={
        isInDropdown
          ? 'flex w-full cursor-pointer flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium transition-colors hover:bg-primary-light/20 focus:bg-primary-light/20 focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg'
          : 'my-1 flex h-10 w-full transform-gpu cursor-pointer flex-row items-center justify-start space-x-4 rounded-md bg-primary-light px-3 py-2 text-left transition-colors hover:scale-[1.02] hover:brightness-125 active:scale-[0.98] active:brightness-125'
      }
      onClick={onClick}
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">{icon}</span>
      <span className="flex items-center font-medium">{label}</span>
    </button>
  ),
);
MenuOption.displayName = 'MenuOption';

export interface AccountMenuContentProps {
  onClose: () => void;
  onRequestLogout: () => void;
  /** When true, do not render LanguageSubmenu (caller adds language above). */
  embedInOverflow?: boolean;
}

const AccountMenuContent = ({
  onClose,
  onRequestLogout,
  embedInOverflow = false,
}: AccountMenuContentProps): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: account } = useAccount();
  const isEmailVerified = useIsEmailVerifiedEffective();
  const subscriptionLabel = useSubscriptionLabel(account?.subscription);

  const handleViewProfile = () => {
    navigate({ to: ROUTE_ACCOUNT });
    onClose();
  };
  const handleManageSubscription = () => {
    navigate({ to: ROUTE_ACCOUNT_SUBSCRIPTIONS });
    onClose();
  };
  const handleContactSupport = () => {
    window.location.href = 'mailto:support@turfmaster.de';
    onClose();
  };

  return (
    <>
      <div className="border-b border-gray-200 px-3 py-2">
        <div className="flex items-center space-x-2">
          <div className="relative shrink-0">
            <AccountProfilePicture
              username={account?.username || ''}
              size="sm"
              className="h-8 w-8"
            />
            {account?.toplistPromotion === 1 ? (
              <div className="absolute -right-1 -bottom-1 z-20">
                <span className="text-sm text-green-500">&#9650;</span>
              </div>
            ) : account?.toplistPromotion === -1 ? (
              <div className="absolute -right-1 -bottom-1 z-20">
                <span className="text-sm text-red-500">&#9660;</span>
              </div>
            ) : null}
          </div>
          <div>
            <div className="flex items-center space-x-2 text-sm font-medium">
              <span>{account?.username}</span>
              {account && !isEmailVerified && (
                <span className="text-xs text-yellow-500" title={t('verify_email_menu_hint')}>
                  ⚠
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">{account?.email}</div>
            {account && !isEmailVerified && (
              <div className="mt-1 text-xs font-medium text-yellow-600">
                {t('verify_email_menu_hint')}
              </div>
            )}
            {subscriptionLabel && (
              <div className="text-xs font-medium text-primary">{subscriptionLabel}</div>
            )}
          </div>
        </div>
      </div>
      <DropdownMenuItem asChild>
        <MenuOption
          icon={<ProfileIcon />}
          label={t('start_accountViewProfile')}
          onClick={handleViewProfile}
          isInDropdown
        />
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <MenuOption
          icon={<PeopleToplistIcon />}
          label={t('start_accountManageSubscription')}
          onClick={handleManageSubscription}
          isInDropdown
        />
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem asChild>
        <MenuOption
          icon={<EmailIcon />}
          label={t('start_accountContactSupport')}
          onClick={handleContactSupport}
          isInDropdown
        />
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {!embedInOverflow && <LanguageSubmenu />}
      {!embedInOverflow && <DropdownMenuSeparator />}
      <DropdownMenuItem asChild>
        <MenuOption
          icon={<LogoutIcon />}
          label={t('start_accountLogout')}
          onClick={onRequestLogout}
          isInDropdown
        />
      </DropdownMenuItem>
    </>
  );
};

export default AccountMenuContent;
